import { useState } from "react";
import { Link } from "react-router-dom";
import { FolderOpen, Plus } from "lucide-react";
import { useProjects } from "@/hooks/use-projects";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { ProjectCreateDialog } from "@/components/projects/project-create-dialog";
import { formatDate } from "@/lib/utils";

export default function ProyectosPage() {
  const projectsQuery = useProjects();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="max-w-container mx-auto px-4 lg:px-6 py-6 space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-ink">
            Proyectos
          </h1>
          <p className="text-sm text-content-muted mt-0.5">
            Cada proyecto agrupa documentos y conversaciones con la IA.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="w-4 h-4" />
          Nuevo proyecto
        </Button>
      </div>

      {projectsQuery.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-36 rounded-xl" />
          ))}
        </div>
      ) : projectsQuery.isError ? (
        <ErrorState
          message={projectsQuery.error.message}
          onRetry={() => projectsQuery.refetch()}
        />
      ) : (projectsQuery.data ?? []).length === 0 ? (
        <EmptyState
          icon={<FolderOpen />}
          title="Sin proyectos todavía"
          description="Crea tu primer proyecto para empezar a subir documentos."
          action={{ label: "Nuevo proyecto", onClick: () => setCreateOpen(true) }}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(projectsQuery.data ?? []).map((project) => (
            <Link key={project.id} to={`/proyectos/${project.id}`}>
              <Card className="h-full hover:shadow-card hover:border-brand/40 transition-all cursor-pointer">
                <CardHeader className="pb-2">
                  <div className="flex items-start gap-2">
                    <FolderOpen className="w-5 h-5 text-brand mt-0.5 flex-shrink-0" />
                    <CardTitle className="text-base font-heading text-ink truncate">
                      {project.nombre}
                    </CardTitle>
                  </div>
                  {project.descripcion && (
                    <CardDescription className="line-clamp-2">
                      {project.descripcion}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-xs text-content-muted">
                    {project.num_documents != null && (
                      <>{project.num_documents} documentos · </>
                    )}
                    Creado el {formatDate(project.created_at)}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <ProjectCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}
